import { apiClient } from './apiClient';
import { BoardType } from './enums';

export interface IntradayBacktestResult {
  strategyName: string;
  symbol: string;
  period: string;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  totalReturn: number;
  maxDrawdown: number;
  trades: {
    entryTime: string;
    entryPrice: number;
    exitTime: string;
    exitPrice: number;
    pnl: number;
    pnlPercent: number;
    reason: string;
    duration: number;
  }[];
}

export interface RunBacktestParams {
  symbol: string;
  strategyId: string;
  period?: string;
  days?: number;
}

export interface StockRecommendation {
  id: number;
  symbol: string;
  name?: string;
  strategyId: string;
  date: string;
  price: number;
  reason?: string;
  score?: number;
  status: 'pending' | 'verified' | 'failed';
  priceT1?: number;
  returnT1?: number;
  priceT3?: number;
  returnT3?: number;
  priceT5?: number;
  returnT5?: number;
  indexReturnT1?: number;
  indexReturnT3?: number;
  indexReturnT5?: number;
  alphaT1?: number;
  alphaT3?: number;
  alphaT5?: number;
  maxReturn?: number;
  minReturn?: number;
  metadata?: any;
  createdAt: string;
}

export interface IntradaySignal {
  id: number;
  symbol: string;
  name?: string;
  strategyId: string;
  time: string; // ISO string
  date: string;
  price: number;
  change?: number;
  volRatio?: number;
  turnoverRate?: number;
  action: 'BUY' | 'SELL';
  reason?: string;
  score?: number;
  metadata?: any;
  status: 'pending' | 'processed' | 'failed';
  createdAt: string;
}

export interface MarketIndex {
  symbol: string;
  name: string;
  value: string;
  changeAmount: number;
  changePercent: number;
  isUp: boolean;
}

export interface BoardPerformance {
  code: string;
  name: string;
  changePercent: number;
  price: number;
  totalCap: number;
  turnoverRate: number;
  riseCount: number;
  fallCount: number;
  leaderName: string;
  leaderChangePercent: number;
}

export interface StockBasic {
  symbol: string;
  name: string;
  industry?: string;
  area?: string;
}

export interface MarketBreadth {
  timestamp: string;
  totalStocks: number;
  aboveMa20Count: number;
  aboveMa20Percent: number;
  aboveMa60Count: number;
  aboveMa60Percent: number;
  bullishMacdCount: number;
  bullishMacdPercent: number;
  breadthScore: number;
  csi300Trend: 'bull' | 'bear' | 'neutral';
}

export interface MarketSentimentData {
  timestamp: string;
  sentimentScore: number; // 0-100
  sentimentDescription: 'Extreme Fear' | 'Fear' | 'Neutral' | 'Greed' | 'Extreme Greed';
  marketBreadth: {
    upCount: number;
    downCount: number;
    flatCount: number;
    limitUpCount: number; // 涨停股数 (考虑板块差异)
    limitDownCount: number; // 跌停股数 (考虑板块差异)
  };
  volumeAnalysis: {
    upVolume: number;
    downVolume: number;
    volumeRatio: number; // upVolume / totalVolume
  };
  technicalBreadth: {
    aboveMa20Percent: number;
    aboveMa60Percent: number;
    breadthScore: number;
  };
}

export interface StockMonitorTask {
  monitorId: string;
  symbol: string;
  name?: string;
  strategyId: string;
  status: string;
  interval: string;
  lastCheckTime?: string;
  analysisScore?: number;
  marketData?: any;
  currentData?: {
    open: string;
    high: string;
    low: string;
    close: string;
    volume: number;
  };
  backtestOptimization?: {
    paramRanges: any;
    bestParams: any;
    results: {
      totalReturn: string;
      annualReturn?: string;
      sharpeRatio: string;
      maxDrawdown: string;
      winRate: string;
      trades: number | string;
      profitFactor?: string;
      [key: string]: any;
    };
    lastRunTime: string;
  };
}

export interface StockQuantPosition {
  id: number;
  symbol: string;
  name?: string;
  entryPrice: number;
  entryTime: string;
  quantity: number;
  status: 'open' | 'closed';
  exitPrice?: number;
  exitTime?: string;
  exitReason?: string;
  pnl?: number;
  pnlPercent?: number;
  riskMultiplier?: number;
  updatedAt: string;
}

export interface QuantPortfolioSummary {
  totalPositions: number;
  activePositions: number;
  closedPositions: number;
  totalPnL: number;
  winRate: number;
  winningPositions: number;
  losingPositions: number;
}

export interface IntradayBacktestResult {
  strategyName: string;
  symbol: string;
  period: string;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  totalReturn: number;
  maxDrawdown: number;
  trades: {
    entryTime: string;
    entryPrice: number;
    exitTime: string;
    exitPrice: number;
    pnl: number;
    pnlPercent: number;
    reason: string;
    duration: number;
  }[];
}

export class StockService {
  /**
   * 获取每日潜力股
   */
  async getDailyPicks(date?: string, limit: number = 20): Promise<StockMonitorTask[]> {
    try {
      const response = await apiClient.get<StockMonitorTask[]>('/stock/monitor/daily-picks', {
        params: { date, limit },
      });

      if (!response.success) {
        throw new Error(response.message || '获取每日潜力股失败');
      }

      return response.data || [];
    } catch (error: any) {
      console.error('Get daily picks error:', error);
      throw error;
    }
  }

  /**
   * 获取板块涨跌幅排名
   */
  async getBoardPerformance(type: BoardType = 'industry', limit: number = 10): Promise<BoardPerformance[]> {
    try {
      const response = await apiClient.get<BoardPerformance[]>('/stock/boards', {
        params: { type, limit },
      });
      return response.data || [];
    } catch (error) {
      console.error('获取板块涨跌幅失败:', error);
      return [];
    }
  }

  /**
   * 获取盘中信号
   */
  async getIntradaySignals(symbol?: string, date?: string, limit: number = 50): Promise<IntradaySignal[]> {
    try {
      const response = await apiClient.get<IntradaySignal[]>('/stock/intraday/signals', {
        params: { symbol, date, limit },
      });
      return response.data;
    } catch (error) {
      console.error('获取盘中信号失败:', error);
      return [];
    }
  }

  /**
   * 获取量化虚拟持仓
   */
  async getQuantPositions(
    symbol?: string,
    status?: 'open' | 'closed',
    limit: number = 50,
  ): Promise<StockQuantPosition[]> {
    try {
      const response = await apiClient.get<StockQuantPosition[]>('/stock/intraday/positions', {
        params: { symbol, status, limit },
      });
      return response.data || [];
    } catch (error) {
      console.error('获取虚拟持仓失败:', error);
      return [];
    }
  }

  /**
   * 获取量化虚拟持仓概览
   */
  async getQuantPortfolioSummary(): Promise<QuantPortfolioSummary> {
    try {
      const response = await apiClient.get<QuantPortfolioSummary>('/stock/intraday/summary');
      return response.data;
    } catch (error) {
      console.error('获取虚拟持仓概览失败:', error);
      return {
        totalPositions: 0,
        activePositions: 0,
        closedPositions: 0,
        totalPnL: 0,
        winRate: 0,
        winningPositions: 0,
        losingPositions: 0,
      };
    }
  }

  /**
   * 获取市场指数行情
   */
  async getMarketIndices(): Promise<MarketIndex[]> {
    try {
      const response = await apiClient.get<MarketIndex[]>('/stock/indices');

      if (!response.success) {
        throw new Error(response.message || '获取指数行情失败');
      }

      return response.data || [];
    } catch (error: any) {
      console.error('Get market indices error:', error);
      throw error;
    }
  }

  /**
   * 获取市场广度
   */
  async getMarketBreadth(): Promise<MarketBreadth> {
    try {
      const response = await apiClient.get<MarketBreadth>('/stock/breadth');

      if (!response.success) {
        throw new Error(response.message || '获取市场广度失败');
      }

      return response.data;
    } catch (error: any) {
      console.error('Get market breadth error:', error);
      throw error;
    }
  }

  /**
   * 获取市场情绪
   */
  async getMarketSentiment(): Promise<MarketSentimentData> {
    try {
      const response = await apiClient.get<MarketSentimentData>('/stock/sentiment');

      if (!response.success) {
        throw new Error(response.message || '获取市场情绪失败');
      }

      return response.data;
    } catch (error: any) {
      console.error('Get market sentiment error:', error);
      throw error;
    }
  }

  /**
   * 获取推荐历史及验证结果
   */
  async getRecommendationHistory(params?: {
    strategyId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: StockRecommendation[]; total: number }> {
    try {
      const response = await apiClient.get<StockRecommendation[]>('/stock/recommendations', {
        params,
      });

      if (!response.success) {
        throw new Error(response.message || '获取推荐历史失败');
      }

      const res = response as any;
      return {
        data: res.data || [],
        total: res.total || 0,
      };
    } catch (error: any) {
      console.error('Get recommendation history error:', error);
      throw error;
    }
  }

  /**
   * 运行盘中策略回测
   */
  async runIntradayBacktest(params: RunBacktestParams): Promise<IntradayBacktestResult> {
    try {
      const response = await apiClient.post<IntradayBacktestResult>('/stock/intraday/backtest', params);

      if (!response.success) {
        throw new Error(response.message || '回测失败');
      }

      return response.data;
    } catch (error: any) {
      console.error('Run intraday backtest error:', error);
      throw error;
    }
  }
}

export const stockService = new StockService();
